/**
 * Multi-language tokenizer for English and French text processing
 * Supports stemming, stop word removal, and normalization for search indexing
 */

// English stop words - comprehensive list for filtering
const ENGLISH_STOP_WORDS = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from', 'has', 'he', 
  'in', 'is', 'it', 'its', 'of', 'on', 'that', 'the', 'to', 'was', 'were', 
  'will', 'with', 'the', 'this', 'but', 'they', 'have', 'had', 'what', 'said', 
  'each', 'which', 'do', 'how', 'their', 'if', 'up', 'out', 'many', 'then', 
  'them', 'these', 'so', 'some', 'her', 'would', 'make', 'like', 'into', 'him', 
  'time', 'two', 'more', 'very', 'when', 'come', 'may', 'see', 'over', 'way', 
  'who', 'oil', 'sit', 'call', 'now', 'find', 'long', 'down', 'day', 'did', 
  'get', 'come', 'made', 'may', 'part'
]);

// French stop words - comprehensive list for filtering
const FRENCH_STOP_WORDS = new Set([
  'le', 'de', 'et', 'à', 'un', 'il', 'être', 'et', 'en', 'avoir', 'que', 'pour',
  'dans', 'ce', 'son', 'une', 'sur', 'avec', 'ne', 'se', 'pas', 'tout', 'plus',
  'par', 'grand', 'en', 'une', 'être', 'et', 'en', 'avoir', 'que', 'pour', 'dans',
  'ce', 'son', 'sur', 'avec', 'ne', 'se', 'pas', 'tout', 'plus', 'par', 'grand',
  'en', 'une', 'être', 'et', 'en', 'avoir', 'que', 'pour', 'dans', 'ce', 'son',
  'sur', 'avec', 'ne', 'se', 'pas', 'tout', 'plus', 'par', 'grand', 'le', 'de',
  'un', 'à', 'être', 'et', 'en', 'avoir', 'que', 'pour', 'dans', 'ce', 'son',
  'une', 'sur', 'avec', 'ne', 'se', 'pas', 'tout', 'plus', 'par', 'grand',
  'en', 'une', 'être', 'et', 'en', 'avoir', 'que', 'pour', 'dans', 'ce', 'son',
  'sur', 'avec', 'ne', 'se', 'pas', 'tout', 'plus', 'par', 'grand', 'où', 'ou',
  'mais', 'qui', 'nous', 'vous', 'ils', 'elles', 'cette', 'ces', 'celui', 'celle',
  'ceux', 'celles', 'très', 'bien', 'encore', 'alors', 'donc', 'ainsi', 'aussi',
  'car', 'comme', 'quand', 'si', 'après', 'avant', 'pendant', 'depuis', 'jusqu',
  'vers', 'chez', 'sans', 'sous', 'entre', 'contre', 'malgré', 'selon', 'parmi'
]);

// Simple English stemmer - Porter-style suffix removal
const ENGLISH_SUFFIXES = [
  { suffix: 'ational', replacement: 'ate' },
  { suffix: 'tional', replacement: 'tion' },
  { suffix: 'enci', replacement: 'ence' },
  { suffix: 'anci', replacement: 'ance' },
  { suffix: 'izer', replacement: 'ize' },
  { suffix: 'abli', replacement: 'able' },
  { suffix: 'alli', replacement: 'al' },
  { suffix: 'entli', replacement: 'ent' },
  { suffix: 'eli', replacement: 'e' },
  { suffix: 'ousli', replacement: 'ous' },
  { suffix: 'ization', replacement: 'ize' },
  { suffix: 'ation', replacement: 'ate' },
  { suffix: 'ator', replacement: 'ate' },
  { suffix: 'alism', replacement: 'al' },
  { suffix: 'iveness', replacement: 'ive' },
  { suffix: 'fulness', replacement: 'ful' },
  { suffix: 'ousness', replacement: 'ous' },
  { suffix: 'aliti', replacement: 'al' },
  { suffix: 'iviti', replacement: 'ive' },
  { suffix: 'biliti', replacement: 'ble' },
  { suffix: 'ing', replacement: '' },
  { suffix: 'ly', replacement: '' },
  { suffix: 'ed', replacement: '' },
  { suffix: 'ies', replacement: 'i' },
  { suffix: 'ied', replacement: 'i' },
  { suffix: 'ies', replacement: 'i' },
  { suffix: 's', replacement: '' }
];

// Simple French stemmer - common suffix removal
const FRENCH_SUFFIXES = [
  { suffix: 'issement', replacement: '' },
  { suffix: 'ment', replacement: '' },
  { suffix: 'tion', replacement: '' },
  { suffix: 'eur', replacement: '' },
  { suffix: 'euse', replacement: '' },
  { suffix: 'ance', replacement: '' },
  { suffix: 'ence', replacement: '' },
  { suffix: 'able', replacement: '' },
  { suffix: 'ible', replacement: '' },
  { suffix: 'ique', replacement: '' },
  { suffix: 'isme', replacement: '' },
  { suffix: 'iste', replacement: '' },
  { suffix: 'ité', replacement: '' },
  { suffix: 'ant', replacement: '' },
  { suffix: 'ent', replacement: '' },
  { suffix: 'er', replacement: '' },
  { suffix: 'ir', replacement: '' },
  { suffix: 'ez', replacement: '' },
  { suffix: 'ons', replacement: '' },
  { suffix: 'ent', replacement: '' },
  { suffix: 'es', replacement: '' },
  { suffix: 'e', replacement: '' },
  { suffix: 's', replacement: '' }
];

/**
 * Detects language of text (simple heuristic-based approach)
 * @param {string} text - Text to analyze
 * @returns {string} - 'en', 'fr', or 'mixed'
 */
export const detectLanguage = (text) => {
  if (!text?.trim()) return 'en'; // Default to English
  
  const normalizedText = text?.toLowerCase()?.replace(/[^\w\s]/g, ' ');
  const words = normalizedText?.split(/\s+/)?.filter(Boolean);
  
  if (words?.length === 0) return 'en';
  
  let englishScore = 0;
  let frenchScore = 0;
  
  // Check against stop words and common patterns
  words?.forEach(word => {
    if (ENGLISH_STOP_WORDS?.has(word)) englishScore++;
    if (FRENCH_STOP_WORDS?.has(word)) frenchScore++;
    
    // English patterns
    if (word?.includes('ing') || word?.includes('tion') || word?.includes('ly')) {
      englishScore += 0.5;
    }
    
    // French patterns  
    if (word?.includes('ment') || word?.includes('ance') || word?.includes('ence')) {
      frenchScore += 0.5;
    }
  });
  
  if (englishScore > frenchScore * 1.5) return 'en';
  if (frenchScore > englishScore * 1.5) return 'fr';
  return 'mixed';
};

/**
 * Applies stemming to a word based on language
 * @param {string} word - Word to stem
 * @param {string} language - Language code ('en' or 'fr')
 * @returns {string} - Stemmed word
 */
export const stemWord = (word, language = 'en') => {
  if (!word || word?.length < 3) return word;
  
  const suffixes = language === 'fr' ? FRENCH_SUFFIXES : ENGLISH_SUFFIXES;
  
  for (const { suffix, replacement } of suffixes) {
    if (word?.endsWith(suffix)) {
      const stemmed = word?.slice(0, -suffix?.length) + replacement;
      if (stemmed?.length >= 2) {
        return stemmed;
      }
    }
  }
  
  return word;
};

/**
 * Normalizes text by removing accents, special characters, and converting to lowercase
 * @param {string} text - Text to normalize
 * @returns {string} - Normalized text
 */
export const normalizeText = (text) => {
  if (!text) return '';
  
  return text
    ?.toLowerCase()
    // Remove accents and diacritics
    ?.normalize('NFD')
    ?.replace(/[\u0300-\u036f]/g, '')
    // Replace multiple spaces/punctuation with single space
    ?.replace(/[^\w\s]/g, ' ')
    ?.replace(/\s+/g, ' ')
    ?.trim();
};

/**
 * Tokenizes text into normalized, filtered, and stemmed tokens
 * @param {string} text - Text to tokenize
 * @param {Object} options - Tokenization options
 * @param {boolean} options.removeStopWords - Whether to remove stop words (default: true)
 * @param {boolean} options.applyStemming - Whether to apply stemming (default: true)
 * @param {string} options.language - Language hint ('en', 'fr', 'auto')
 * @param {number} options.minLength - Minimum token length (default: 2)
 * @returns {Array<string>} - Array of processed tokens
 */
export const tokenize = (text, options = {}) => {
  const {
    removeStopWords = true,
    applyStemming = true,
    language = 'auto',
    minLength = 2
  } = options;
  
  if (!text?.trim()) return [];
  
  // Detect language if auto
  const detectedLanguage = language === 'auto' ? detectLanguage(text) : language;
  
  // Normalize text
  const normalized = normalizeText(text);
  
  // Split into tokens
  const tokens = normalized?.split(/\s+/)?.filter(Boolean);
  
  // Process tokens
  const processed = tokens
    ?.filter(token => token?.length >= minLength)
    ?.map(token => {
      // Apply stemming if enabled
      if (applyStemming) {
        if (detectedLanguage === 'mixed') {
          // For mixed language, try both stemmers and pick shorter result
          const englishStem = stemWord(token, 'en');
          const frenchStem = stemWord(token, 'fr');
          return englishStem?.length <= frenchStem?.length ? englishStem : frenchStem;
        } else {
          return stemWord(token, detectedLanguage);
        }
      }
      return token;
    })
    ?.filter(token => {
      // Remove stop words if enabled
      if (removeStopWords) {
        if (detectedLanguage === 'fr' || detectedLanguage === 'mixed') {
          if (FRENCH_STOP_WORDS?.has(token)) return false;
        }
        if (detectedLanguage === 'en' || detectedLanguage === 'mixed') {
          if (ENGLISH_STOP_WORDS?.has(token)) return false;
        }
      }
      return token?.length >= minLength;
    });
  
  // Remove duplicates while preserving order
  return [...new Set(processed)];
};

/**
 * Generates n-grams from tokens for phrase matching
 * @param {Array<string>} tokens - Array of tokens
 * @param {number} n - N-gram size (default: 2)
 * @returns {Array<string>} - Array of n-grams
 */
export const generateNGrams = (tokens, n = 2) => {
  if (!tokens || tokens?.length < n) return [];
  
  const ngrams = [];
  for (let i = 0; i <= tokens?.length - n; i++) {
    ngrams?.push(tokens?.slice(i, i + n)?.join(' '));
  }
  
  return ngrams;
};

/**
 * Tokenizes multiple fields with different weights and processing options
 * @param {Object} fields - Object with field names as keys and text as values
 * @param {Object} fieldOptions - Processing options per field
 * @returns {Object} - Object with field names as keys and token arrays as values
 */
export const tokenizeFields = (fields, fieldOptions = {}) => {
  const result = {};
  
  Object?.entries(fields)?.forEach(([fieldName, fieldText]) => {
    const options = fieldOptions?.[fieldName] || {};
    result[fieldName] = tokenize(fieldText, options);
  });
  
  return result;
};

/**
 * Creates a weighted token frequency map from multiple fields
 * @param {Object} tokenizedFields - Object with field names and token arrays
 * @param {Object} fieldWeights - Weights for each field (default: 1.0)
 * @returns {Map<string, number>} - Map of tokens to weighted frequencies
 */
export const createWeightedTokenMap = (tokenizedFields, fieldWeights = {}) => {
  const tokenMap = new Map();
  
  Object?.entries(tokenizedFields)?.forEach(([fieldName, tokens]) => {
    const weight = fieldWeights?.[fieldName] || 1.0;
    
    tokens?.forEach(token => {
      const currentWeight = tokenMap?.get(token) || 0;
      tokenMap?.set(token, currentWeight + weight);
    });
  });
  
  return tokenMap;
};

export default {
  detectLanguage,
  stemWord,
  normalizeText,
  tokenize,
  generateNGrams,
  tokenizeFields,
  createWeightedTokenMap
};